import React, { useState, memo, useEffect, useCallback, useRef } from 'react'
import moment from 'moment'
import { DragDropContext } from 'react-beautiful-dnd'
import { withStyles } from '@material-ui/core/styles'
import { useDispatch } from 'react-redux'
import { withRouter } from 'react-router-dom'

import Box from '@material-ui/core/Box'
import Col from 'react-bootstrap/lib/Col'
import { SearchInput } from 'Components/Header/SearchInput'
import Progress from 'Components/Progress'
import { SelectDashboard } from '../../../../../Components/Form'
import PipelineColumn from './components/PipelineColumn'
import RefusedModalBody from './components/RefusedModalBody'
import { ColumnsFilter } from './components/ColumnsFilter/ColumnsFilter'
import {
  showReduxModal,
  hideReduxModal,
  resetReduxModal,
} from 'Reducers/UI/actions'
import { pushApiError } from 'Reducers/Errors/actions'
import { downloadStringAsFile } from 'Utils/Misc'
import useGraphQLFetch from '../../../../../Utils/Hooks/useGraphQLFetch'
import { getPitchUrl } from '../../../../../Module/graphQL'
import { escapeCsvString } from 'shared/csv'
import { useAmbassadorsRequest } from '../gql'
import {
  filterPipelines,
  formatUserTrackingData,
  getPreviousStage,
} from './utils'
import {
  BRAND_AMBASSADOR_STATUS_ENUM,
  QUIZ_TYPES,
  STATUS_BOOKED_MEETING,
  STATUS_ON_SITE_TRAINING,
} from '../../../../../shared/constants'

import {
  STATUS_PENDING,
  STATUS_ACTIVE,
  STATUS_PRE_ACCESS,
  STATUS_UNINTERESTED,
  STATUS_WAITING_FOR_REVIEW,
  STATUS_BLOCKED,
  STATUS_QUIZ_IN_PROGRESS,
  STATUS_VIDEO_ONBOARDING_IN_PROGRESS,
  STATUS_ONSITE_MEETING,
  BRAND_AMBASSADORS_STATUS_ENUM_MAP,
} from 'shared/constants'
import {
  restrictedMissionTypeOptions,
  WITHHOLD_OPTIONS_LABEL_ENUM,
  WITHHOLD_OPTIONS_VALUE_ENUM,
  withholdPayOptions,
} from './options'

import style from './style'
import { USER_PROFILE_LOGS_TYPE, COUNTRY_CODE } from '../../../../../constants'
import { createUserProfileLog } from '../../Ambasador/gql'
import { authorization } from '../../../../../Module/Auth/Auth'
import DocumentCheckModalBody from './components/DocumentCheckModalBody'
import OnSiteTrainingModal from './components/OnSiteTrainingModal'

const PIPELINE_COLUMN_LABELS = {
  uninterested: 'Uninterested',
  interested: 'Interested',
  booked: 'Booked',
  videoOnboarding: 'Video Onboarding',
  test: 'Test',
  refused: 'Blocked',
  onSiteTraining: 'Onsite Training',
  preAccess: 'Pre Access',
  finalReview: 'Waiting for Review',
  onsiteMeeting: 'Onsite Meeting',
  approved: 'Approved',
}

const cache = {}
function AmbassadorsPipelineView({
  countryCode,
  classes,
  cityId,
  isOnsiteTrainingEnabled,
  onMailClick,
  cityTimezone,
  match: { url },
}) {
  const isESCountry = countryCode === COUNTRY_CODE.SPAIN

  const FETCH_BA_STATUS = {
    EMPTY: 'empty',
    LOADING: 'loading',
    LOADED: 'loaded',
  }

  const [pipelines, setPipelines] = useState({
    uninterested: [],
    interested: [],
    booked: [],
    videoOnboarding: [],
    test: [],
    refused: [],
    onSiteTraining: [],
    preAccess: [],
    finalReview: [],
    onsiteMeeting: [],
    approved: [],
  })
  const [columnsState, setColumnsState] = useState({
    uninterested: false,
    interested: false,
    booked: true,
    videoOnboarding: true,
    test: true,
    refused: true,
    onSiteTraining: false,
    preAccess: true,
    finalReview: true,
    onsiteMeeting: true,
    approved: true,
  })
  const [uninterestedBAStatus, setUninterestedBAStatus] = useState(
    FETCH_BA_STATUS.EMPTY,
  )
  const [interestedBAStatus, setInterestedBAStatus] = useState(
    FETCH_BA_STATUS.EMPTY,
  )
  const [fetchedEvents, setFetchedEvents] = useState([])
  const [hasQuiz, setHasQuiz] = useState(false)
  const dispatch = useDispatch()
  const user = authorization.getUser()
  const isCityManagerAssistant = authorization.isCityManagerAssistant()

  const oldPipelines = useRef({})
  const {
    progressOfGetAllAmbassadors,
    getAllAmbassadors,
    getAmbassadorCalendarEvents,
    updateAmbassador,
    deleteAmbassador,
  } = useAmbassadorsRequest(cityId)
  const [ambassadorsTransformed, setAmbassadorsTransformed] = useState(false)
  const cachePipelines = useRef(false)

  const fetchArgs = {
    type: QUIZ_TYPES.ONBOARDING_PITCH,
    cityId,
  }

  const { data, isLoaded } = useGraphQLFetch({
    fetchFn: getPitchUrl,
    defaultFetchArgs: fetchArgs,
    fetchId: 'getPitchUrl',
    mapResponse: data => data.quiz,
  })

  useEffect(() => {
    if (isLoaded) {
      if (data.url && data.questionCount) {
        setHasQuiz(true)
      }
    }
  }, [isLoaded])

  useEffect(() => {
    if (cachePipelines.current) {
      cache[cityId + 'ambassadors'] = pipelines
      cachePipelines.current = false
    }
  }, [pipelines])

  const setPipelinesHandler = pipelines => {
    cachePipelines.current = true
    setPipelines(pipelines)
  }

  const fetchAmbassadors = async () => {
    if (cache[cityId + 'ambassadors']) {
      setPipelines(cache[cityId + 'ambassadors'])
      setAmbassadorsTransformed(true)
    } else {
      const [
        { ambassadors: nonBlockedAmbassadors },
        events,
      ] = await Promise.all([
        getAllAmbassadors({
          statuses: [
            BRAND_AMBASSADOR_STATUS_ENUM.VIDEO_ONBOARDING_IN_PROGRESS,
            BRAND_AMBASSADOR_STATUS_ENUM.QUIZ_IN_PROGRESS,
            BRAND_AMBASSADOR_STATUS_ENUM.WAITING_FOR_REVIEW,
            BRAND_AMBASSADOR_STATUS_ENUM.ONSITE_MEETING,
            BRAND_AMBASSADOR_STATUS_ENUM.PRE_ACCESS,
            BRAND_AMBASSADOR_STATUS_ENUM.ACTIVE,
            BRAND_AMBASSADOR_STATUS_ENUM.BOOKED_MEETING,
            ...(isOnsiteTrainingEnabled
              ? [BRAND_AMBASSADOR_STATUS_ENUM.ON_SITE_TRAINING]
              : []),
          ],
        }),
        getAmbassadorCalendarEvents(),
      ])

      const nonBlockedPipelines = convertAmbassadorsToPipelines(
        nonBlockedAmbassadors,
        events,
      )

      setPipelinesHandler(nonBlockedPipelines)
      setAmbassadorsTransformed(true)

      const { ambassadors: blockedAmbassadors } = await getAllAmbassadors(
        {
          statuses: 'BLOCKED',
        },
        { progressName: 'blocked' },
      )

      const blockedPipelines = convertAmbassadorsToPipelines(
        blockedAmbassadors,
        events,
      )

      const allPipelines = {
        ...nonBlockedPipelines,
        refused: blockedPipelines['refused'],
      }
      setPipelinesHandler(prevPipeline => ({
        ...prevPipeline,
        refused: blockedPipelines['refused'],
      }))
      oldPipelines.current = allPipelines
      setFetchedEvents(events)
    }
  }

  const fetchUninterestedAmbassadors = async () => {
    const { ambassadors: uninterestedAmbassadors } = await getAllAmbassadors(
      {
        statuses: BRAND_AMBASSADOR_STATUS_ENUM.UNINTERESTED,
      },
      { progressName: 'uninterested' },
    )
    const uninterestedPipelines = convertAmbassadorsToPipelines(
      uninterestedAmbassadors,
      fetchedEvents,
    )
    const allPipelines = {
      ...pipelines,
      uninterested: uninterestedPipelines['uninterested'],
    }

    setPipelinesHandler(prevPipeline => ({
      ...prevPipeline,
      uninterested: uninterestedPipelines['uninterested'],
    }))

    oldPipelines.current = allPipelines
    setUninterestedBAStatus(FETCH_BA_STATUS.LOADED)
  }

  const fetchInterestedAmbassadors = async () => {
    const { ambassadors: interestedAmbassadors } = await getAllAmbassadors(
      {
        statuses: BRAND_AMBASSADOR_STATUS_ENUM.PENDING,
      },
      { progressName: 'interested' },
    )
    const interestedPipelines = convertAmbassadorsToPipelines(
      interestedAmbassadors,
      fetchedEvents,
    )
    const allPipelines = {
      ...pipelines,
      interested: interestedPipelines['interested'],
    }

    setPipelinesHandler(prevPipeline => ({
      ...prevPipeline,
      interested: interestedPipelines['interested'],
    }))

    oldPipelines.current = allPipelines
    setInterestedBAStatus(FETCH_BA_STATUS.LOADED)
  }

  useEffect(() => {
    fetchAmbassadors()
  }, [isOnsiteTrainingEnabled])

  useEffect(() => {
    if (isOnsiteTrainingEnabled) {
      setColumnsState(prevState => ({
        ...prevState,
        onSiteTraining: true,
      }))
    }
  }, [isOnsiteTrainingEnabled])

  useEffect(() => {
    if (
      columnsState.uninterested &&
      uninterestedBAStatus === FETCH_BA_STATUS.EMPTY
    ) {
      setUninterestedBAStatus(FETCH_BA_STATUS.LOADING)
      fetchUninterestedAmbassadors()
    }

    if (
      columnsState.interested &&
      interestedBAStatus === FETCH_BA_STATUS.EMPTY
    ) {
      setInterestedBAStatus(FETCH_BA_STATUS.LOADING)
      fetchInterestedAmbassadors()
    }
  }, [columnsState])

  const convertAmbassadorsToPipelines = useCallback((ambassadors, events) => {
    const pipelines = ambassadors.reduce(
      (acc, item) => {
        if (!item) return acc

        item.memberSinceFormatted = item.memberSince
          ? dateFormatter(item.memberSince)
          : ''

        item.interviewTimestampFormatted = formatUserTrackingData(
          item,
          'interviewTimestamp',
        )

        const index = events.findIndex(
          eventItem => eventItem.userId === item.id,
        )

        var updatedItem = ~index ? { ...item, ...events[index] } : item
        if (~index) {
          events.splice(index, 1)
        }

        if (BRAND_AMBASSADORS_STATUS_ENUM_MAP[item.status] === STATUS_BLOCKED) {
          acc.refused.push({
            ...updatedItem,
            onChangeStep: formatUserTrackingData(
              item,
              'stepBlockedTimestamp',
              cityTimezone,
            ),
            fromVideoOnboarding:
              item.tracking &&
              !!item.tracking.stepVideoOnboardingInProgressTimestamp,
          })
        }

        if (
          BRAND_AMBASSADORS_STATUS_ENUM_MAP[item.status] === STATUS_PRE_ACCESS
        ) {
          acc.preAccess.push({
            ...updatedItem,
            onChangeStep: formatUserTrackingData(
              item,
              'stepPreAccessTimestamp',
              cityTimezone,
            ),
            fromVideoOnboarding:
              item.tracking &&
              !!item.tracking.stepVideoOnboardingInProgressTimestamp,
          })
        }

        if (
          BRAND_AMBASSADORS_STATUS_ENUM_MAP[item.status] ===
          STATUS_WAITING_FOR_REVIEW
        ) {
          acc.finalReview.push({
            ...updatedItem,
            onChangeStep: formatUserTrackingData(
              item,
              'stepWaitingForReviewTimestamp',
              cityTimezone,
            ),
            fromVideoOnboarding:
              item.tracking &&
              !!item.tracking.stepVideoOnboardingInProgressTimestamp,
          })
        }

        if (BRAND_AMBASSADORS_STATUS_ENUM_MAP[item.status] === STATUS_ACTIVE) {
          acc.approved.push({
            ...updatedItem,
            onChangeStep: formatUserTrackingData(
              item,
              'stepApprovedTimestamp',
              cityTimezone,
            ),
            fromVideoOnboarding:
              item.tracking &&
              !!item.tracking.stepVideoOnboardingInProgressTimestamp,
          })
        }

        if (
          STATUS_BOOKED_MEETING ===
          BRAND_AMBASSADORS_STATUS_ENUM_MAP[item.status]
        ) {
          acc.booked.push({
            ...updatedItem,
            onChangeStep: formatUserTrackingData(
              item,
              'stepBookedTimestamp',
              cityTimezone,
            ),
            fromVideoOnboarding:
              item.tracking &&
              !!item.tracking.stepVideoOnboardingInProgressTimestamp,
          })
        }

        if (STATUS_PENDING === BRAND_AMBASSADORS_STATUS_ENUM_MAP[item.status]) {
          if (!~index) {
            acc.interested.push({
              ...updatedItem,
              onChangeStep: formatUserTrackingData(
                item,
                'stepInterestedTimestamp',
                cityTimezone,
              ),
              fromVideoOnboarding:
                item.tracking &&
                !!item.tracking.stepVideoOnboardingInProgressTimestamp,
            })
          }

          if (~index) {
            acc.booked.push({
              ...updatedItem,
              onChangeStep: formatUserTrackingData(
                item,
                'stepBookedTimestamp',
                cityTimezone,
              ),
              fromVideoOnboarding:
                item.tracking &&
                !!item.tracking.stepVideoOnboardingInProgressTimestamp,
            })
          }
        }

        if (
          BRAND_AMBASSADORS_STATUS_ENUM_MAP[item.status] ===
          STATUS_QUIZ_IN_PROGRESS
        ) {
          acc.test.push({
            ...updatedItem,
            onChangeStep: formatUserTrackingData(
              item,
              'stepQuizInProgressTimestamp',
              cityTimezone,
            ),
            fromVideoOnboarding:
              item.tracking &&
              !!item.tracking.stepVideoOnboardingInProgressTimestamp,
          })
        }

        if (
          BRAND_AMBASSADORS_STATUS_ENUM_MAP[item.status] ===
          STATUS_VIDEO_ONBOARDING_IN_PROGRESS
        ) {
          acc.videoOnboarding.push({
            ...updatedItem,
            onChangeStep: formatUserTrackingData(
              item,
              'stepVideoOnboardingInProgressTimestamp',
              cityTimezone,
            ),
            fromVideoOnboarding:
              item.tracking &&
              !!item.tracking.stepVideoOnboardingInProgressTimestamp,
          })
        }

        if (
          BRAND_AMBASSADORS_STATUS_ENUM_MAP[item.status] === STATUS_UNINTERESTED
        ) {
          acc.uninterested.push({
            ...updatedItem,
            onChangeStep: formatUserTrackingData(
              item,
              'stepUninterestedTimestamp',
              cityTimezone,
            ),
            fromVideoOnboarding:
              item.tracking &&
              !!item.tracking.stepVideoOnboardingInProgressTimestamp,
            previousStage: getPreviousStage(item.tracking),
          })
        }

        if (
          BRAND_AMBASSADORS_STATUS_ENUM_MAP[item.status] ===
          STATUS_ON_SITE_TRAINING
        ) {
          acc.onSiteTraining.push({
            ...updatedItem,
            onChangeStep: formatUserTrackingData(
              item,
              'stepOnSiteTrainingTimestamp',
              cityTimezone,
              true,
            ),
            fromVideoOnboarding:
              item.tracking &&
              !!item.tracking.stepVideoOnboardingInProgressTimestamp,
            previousStage: getPreviousStage(item.tracking),
          })
        }

        if (
          BRAND_AMBASSADORS_STATUS_ENUM_MAP[item.status] ===
          STATUS_ONSITE_MEETING
        ) {
          acc.onsiteMeeting.push({
            ...updatedItem,
            onChangeStep: formatUserTrackingData(
              item,
              'stepOnsiteMeetingTimestamp',
              cityTimezone,
            ),
            fromVideoOnboarding:
              item.tracking &&
              !!item.tracking.stepVideoOnboardingInProgressTimestamp,
          })
        }
        return acc
      },
      {
        uninterested: [],
        interested: [],
        booked: [],
        videoOnboarding: [],
        test: [],
        refused: [],
        onSiteTraining: [],
        preAccess: [],
        finalReview: [],
        onsiteMeeting: [],
        approved: [],
      },
    )

    const pipelinesToTrackingTsMap = {
      approved: 'stepApprovedTimestamp',
      booked: 'stepBookedTimestamp',
      finalReview: 'stepWaitingForReviewTimestamp',
      interested: 'stepInterestedTimestamp',
      refused: 'stepBlockedTimestamp',
      test: 'stepQuizInProgressTimestamp',
      onSiteTraining: 'stepOnSiteTrainingTimestamp',
      onsiteMeeting: 'stepOnsiteMeetingTimestamp',
      preAccess: 'stepPreAccessTimestamp',
      videoOnboarding: 'stepVideoOnboardingInProgressTimestamp',
    }
    const sortedPipelines = Object.entries(pipelines).reduce(
      (acc, pipelineEntry) => {
        const pipelineName = pipelineEntry[0]
        const bas = pipelineEntry[1]
        const sortingKey = pipelinesToTrackingTsMap[pipelineName]
        const sortedBas = bas.sort((baA, baB) => {
          const valueOne = baA.tracking[sortingKey]
            ? Number(baA.tracking[sortingKey])
            : new Date(baA.memberSince)
          const valueTwo = baA.tracking[sortingKey]
            ? Number(baB.tracking[sortingKey])
            : new Date(baB.memberSince)
          return valueTwo - valueOne
        })
        acc[pipelineName] = sortedBas
        return acc
      },
      {},
    )

    return sortedPipelines
  }, [])

  const onExportData = useCallback(column => {
    const exportHeaders = [
      'Id',
      'First Name',
      'Last Name',
      'Email',
      'Recruitment Channel',
      'Mobile Phone',
      'Rating',
      'Member Since',
      'Onboarding Session',
      'City',
      'Status',
    ]

    let content = exportHeaders.join(',')
    column.forEach(item => {
      const rowData = {
        'Id': item.id,
        'First Name': item.firstName,
        'Last Name': item.lastName,
        'Recruitment Channel': item.recruitmentChannel,
        'Email': item.email,
        'Mobile Phone': item.mobilePhone,
        'Rating': item.rating,
        'Member Since': item.memberSinceFormatted,
        'Onboarding Session': item.interviewTimestampFormatted,
        'City': item.city.name,
        'Missions': item.missions && item.missions.totalCount,
        'Status': item.status,
      }
      content +=
        '\n' +
        exportHeaders
          .map(headerName => {
            const value = rowData[headerName]
            return typeof value === 'string' ? escapeCsvString(value) : value
          })
          .join(',')
    })
    downloadStringAsFile(content, `city_${cityId}.csv`, 'text/csv')
  })

  const transfer = (from, to, item) => {
    const sourceClone = [...pipelines[from]]
    const destinationClone = [...pipelines[to]]
    sourceClone.splice(item.index, 1)
    destinationClone.unshift(item)
    setPipelinesHandler({
      ...pipelines,
      [from]: sourceClone,
      [to]: destinationClone,
    })
  }

  const reset = () => {
    setPipelinesHandler(oldPipelines.current)
  }

  const handlerDragEnd = useCallback(
    async result => {
      const { source, destination, draggableId: userId } = result

      if (validateStatusMovement(source, destination, userId, pipelines)) {
        oldPipelines.current = pipelines

        const { itemData, index } = pipelines[source.droppableId].reduce(
          (acc, item, index) => {
            if (item.id == userId) return { itemData: item, index }
            return acc
          },
          {
            itemData: {},
            index: 0,
          },
        )

        const item = {
          ...itemData,
          index,
          from: source.droppableId,
          to: destination.droppableId,
        }

        if (destination.droppableId === 'refused') {
          handleRefusedModalOpen(item)
        } else if (destination.droppableId === 'test') {
          transfer(source.droppableId, 'test', {
            ...item,
            status: BRAND_AMBASSADOR_STATUS_ENUM.QUIZ_IN_PROGRESS,
          })
          try {
            await updateAmbassador(item.id, {
              statusV2: STATUS_QUIZ_IN_PROGRESS,
            })
          } catch (error) {
            reset()
          }
        } else if (destination.droppableId === 'preAccess') {
          if (source.droppableId === 'onSiteTraining') {
            return handleOnPreAccesFromOnsiteTraining(item)
          }
          transfer(source.droppableId, 'preAccess', {
            ...item,
            status: BRAND_AMBASSADOR_STATUS_ENUM.PRE_ACCESS,
            onChangeStep: moment().format('DD MMM YYYY, HH:mm'),
            tracking: {
              ...item.tracking,
              stepPreAccessTimestamp: new Date().getTime().toString(),
            },
          })
          try {
            await updateAmbassador(item.id, {
              statusV2: STATUS_PRE_ACCESS,
            })
          } catch (error) {
            reset()
          }
        } else if (destination.droppableId === 'approved') {
          handleApprovedModalOpen({
            item,
            isHideActiveButtons: false,
          })
        } else if (destination.droppableId === 'onSiteTraining') {
          transfer(source.droppableId, 'onSiteTraining', {
            ...item,
            status: BRAND_AMBASSADOR_STATUS_ENUM.ON_SITE_TRAINING,
            onChangeStep: moment().format('DD MMM YYYY, HH:mm'),
            tracking: {
              ...item.tracking,
              stepOnSiteTrainingTimestamp: new Date().getTime().toString(),
            },
          })
          try {
            await updateAmbassador(item.id, {
              statusV2: STATUS_ON_SITE_TRAINING,
            })
          } catch (error) {
            reset()
          }
        } else if (destination.droppableId === 'onsiteMeeting') {
          transfer(source.droppableId, 'onsiteMeeting', {
            ...item,
            status: BRAND_AMBASSADOR_STATUS_ENUM.ONSITE_MEETING,
            onChangeStep: moment().format('DD MMM YYYY, HH:mm'),
            tracking: {
              ...item.tracking,
              stepOnsiteMeetingTimestamp: new Date().getTime().toString(),
            },
          })
          try {
            await updateAmbassador(item.id, {
              statusV2: STATUS_ONSITE_MEETING,
            })
          } catch (error) {
            reset()
          }
        } else if (destination.droppableId === 'uninterested') {
          transfer(source.droppableId, 'uninterested', {
            ...item,
            status: BRAND_AMBASSADOR_STATUS_ENUM.UNINTERESTED,
            previousStage:
              source.droppableId.charAt(0).toUpperCase() +
              source.droppableId.slice(1),
            onChangeStep: moment().format('DD MMM YYYY, HH:mm'),
            tracking: {
              ...item.tracking,
              stepUninterestedTimestamp: new Date().getTime().toString(),
            },
          })
          try {
            await updateAmbassador(item.id, {
              statusV2: STATUS_UNINTERESTED,
            })
          } catch (error) {
            reset()
          }
        } else if (destination.droppableId === 'interested') {
          if (item.previousStage) {
            delete item.previousStage
          }
          transfer(source.droppableId, 'interested', {
            ...item,
            status: BRAND_AMBASSADOR_STATUS_ENUM.INTERESTED,
            onChangeStep: moment().format('DD MMM YYYY, HH:mm'),
          })
          try {
            await updateAmbassador(item.id, {
              statusV2: STATUS_PENDING,
            })
          } catch (error) {
            reset()
          }
        } else if (destination.droppableId === 'booked') {
          if (item.previousStage) {
            delete item.previousStage
          }
          transfer(source.droppableId, 'booked', {
            ...item,
            status: BRAND_AMBASSADOR_STATUS_ENUM.BOOKED_MEETING,
          })
          try {
            await updateAmbassador(item.id, {
              statusV2: STATUS_BOOKED_MEETING,
            })
          } catch (error) {
            reset()
          }
        }
      } else if (
        destination &&
        destination.droppableId !== source.droppableId
      ) {
        // Surface disallowed moves instead of silently snapping the card back
        const label = key => PIPELINE_COLUMN_LABELS[key] || key
        dispatch(
          pushApiError(
            `Moving a BA from "${label(source.droppableId)}" to "${label(
              destination.droppableId,
            )}" is not allowed`,
          ),
        )
      }
    },
    [pipelines],
  )

  const handlerSearch = useCallback(
    searchValue => {
      if (!searchValue || !searchValue.length)
        return setPipelines(cache[cityId + 'ambassadors'])

      setPipelines(
        Object.entries(cache[cityId + 'ambassadors']).reduce(
          (acc, [key, value]) => {
            acc[key] = value.filter(item =>
              `${item.lastName} ${item.firstName}`
                .toLowerCase()
                .includes(searchValue.toLowerCase()),
            )
            return acc
          },
          {
            uninterested: [],
            interested: [],
            booked: [],
            videoOnboarding: [],
            test: [],
            onSiteTraining: [],
            refused: [],
            preAccess: [],
            finalReview: [],
            onsiteMeeting: [],
            approved: [],
          },
        ),
      )
    },
    [pipelines],
  )

  const handleRefusedModalOpen = useCallback(
    async item => {
      const onSubmit = async data => {
        transfer(item.from, 'refused', {
          ...item,
          status: 'BLOCKED',
        })
        try {
          await updateAmbassador(item.id, data)
          await createUserProfileLog({
            input: {
              profile_user_id: +item.id,
              actioned_by_user_id: +user.id,
              type: USER_PROFILE_LOGS_TYPE.BLOCK,
            },
          })
        } catch (error) {
          reset()
        }
        onCancel()
      }

      const onCancel = () => {
        dispatch(hideReduxModal())
        setTimeout(() => dispatch(resetReduxModal()), 100)
      }

      dispatch(
        showReduxModal({
          title: 'Blocked reason',
          hideButtons: true,
          children: (
            <RefusedModalBody onSubmit={onSubmit} onCancel={onCancel} />
          ),
        }),
      )
    },
    [pipelines],
  )

  const handleOnPreAccesFromOnsiteTraining = useCallback(
    async item => {
      const onSubmit = async data => {
        transfer(item.from, 'preAccess', {
          ...item,
          status: BRAND_AMBASSADOR_STATUS_ENUM.PRE_ACCESS,
          onChangeStep: moment().format('DD MMM YYYY, HH:mm'),
          tracking: {
            ...item.tracking,
            stepPreAccessTimestamp: new Date().getTime().toString(),
          },
        })
        try {
          await updateAmbassador(item.id, data)
        } catch (error) {
          reset()
        }
        onCancel()
      }

      const onCancel = () => {
        dispatch(hideReduxModal())
        setTimeout(() => dispatch(resetReduxModal()), 100)
      }
      dispatch(
        showReduxModal({
          title: 'BA Onsite training completion rating',
          titleFontSize: '22px',
          hideButtons: true,
          aspect: 'nonScrollable',
          children: (
            <OnSiteTrainingModal
              onSubmit={onSubmit}
              onCancel={onCancel}
              ratedByUserId={user.id}
              userRated={item.id}
            />
          ),
        }),
      )
    },
    [pipelines],
  )

  const handleDelete = useCallback(
    async item => {
      dispatch(
        showReduxModal({
          title: 'Confirmation needed',
          onConfirm: async () => {
            const sourceClone = [...pipelines[item.columnName]]
            const index = sourceClone.findIndex(
              sourceItem => sourceItem.id === item.id,
            )
            sourceClone.splice(index, 1)
            setPipelinesHandler({
              ...pipelines,
              [item.columnName]: sourceClone,
            })
            try {
              await deleteAmbassador(item.id)
            } catch (error) {
              reset()
            }
          },
          children: (
            <Box
              display="flex"
              flexWrap="wrap"
              justifyContent="center"
              mt={3}
              mb={3}
            >
              Are you sure you want to delete {item.firstName} {item.lastName}?
            </Box>
          ),
        }),
      )
    },
    [pipelines],
  )

  const handleApprovedModalOpen = useCallback(
    ({ item, isHideActiveButtons }) => {
      const handleApprove = async () => {
        transfer(item.from, 'approved', {
          ...item,
          status: BRAND_AMBASSADOR_STATUS_ENUM.ACTIVE,
        })
        try {
          await updateAmbassador(item.id, {
            statusV2: STATUS_ACTIVE,
          })
          await createUserProfileLog({
            input: {
              profile_user_id: +item.id,
              actioned_by_user_id: +user.id,
              type: USER_PROFILE_LOGS_TYPE.APPROVE,
            },
          })
        } catch (error) {
          reset()
        }
        handleCancel()
      }

      const handleCancel = () => {
        dispatch(hideReduxModal())
        setTimeout(() => dispatch(resetReduxModal()), 100)
      }

      const handleRefuse = async () => {
        handleRefusedModalOpen(item)
      }

      dispatch(
        showReduxModal({
          title: (
            <div>
              <span>Document review</span>
              <br />
              <span>
                {item.lastName} {item.firstName}
              </span>
            </div>
          ),
          hideButtons: true,
          children: (
            <DocumentCheckModalBody
              onRefuse={handleRefuse}
              onCancel={handleRefuse}
              onApprove={handleApprove}
              item={item}
              isHideActiveButtons={isHideActiveButtons}
            />
          ),
        }),
      )
    },
    [pipelines],
  )

  const shouldShowColumn = key => {
    if (
      (!hasQuiz && key === 'test') ||
      (key === 'onSiteTraining' && !isOnsiteTrainingEnabled)
    ) {
      return false
    } else {
      return true
    }
  }

  const onWithHoldChange = useCallback(
    (event, key, value) => {
      if (value === WITHHOLD_OPTIONS_VALUE_ENUM.ALL) {
        return setPipelines(cache[cityId + 'ambassadors'])
      } else if (value === WITHHOLD_OPTIONS_VALUE_ENUM.ON) {
        const filteredPipelines = filterPipelines(
          cache[cityId + 'ambassadors'],
          item => item.withholdPay,
        )
        setPipelines(filteredPipelines)
      } else if (value === WITHHOLD_OPTIONS_VALUE_ENUM.OFF) {
        const filteredPipelines = filterPipelines(
          cache[cityId + 'ambassadors'],
          item => !item.withholdPay,
        )
        setPipelines(filteredPipelines)
      }
    },
    [pipelines],
  )

  const onRestrictedMissionTypeChange = useCallback(
    (event, key, value) => {
      if (value === null) {
        return setPipelines(cache[cityId + 'ambassadors'])
      } else {
        const filteredPipelines = filterPipelines(
          cache[cityId + 'ambassadors'],
          item => item.restrictedMissionType === value,
        )
        setPipelines(filteredPipelines)
      }
    },
    [pipelines],
  )

  const handleAttendantUpdate = (key, id, isChecked) => {
    const sourceClone = [...pipelines[key]]
    setPipelinesHandler({
      ...pipelines,
      [key]: sourceClone.map(item => {
        if (item.id === id)
          return {
            ...item,
            isAttendantOnboarding: isChecked,
          }
        return item
      }),
    })
  }

  return (
    <div className={classes.wrap}>
      <div className={classes.wrapForm}>
        <Col xs={4}>
          <SearchInput placeholder="Search" searchHandler={handlerSearch} />
        </Col>
        {!isCityManagerAssistant ? (
          <Col xs={3}>
            <SelectDashboard
              label="Withhold"
              options={withholdPayOptions}
              value={WITHHOLD_OPTIONS_LABEL_ENUM.ALL}
              onChange={onWithHoldChange}
            />
          </Col>
        ) : null}
        <Col xs={3}>
          <SelectDashboard
            label="Mission Type"
            options={restrictedMissionTypeOptions}
            value={null}
            onChange={onRestrictedMissionTypeChange}
          />
        </Col>
      </div>
      <ColumnsFilter
        columns={Object.keys(pipelines).filter(shouldShowColumn)}
        columnsState={columnsState}
        setColumnsState={setColumnsState}
      />
      <DragDropContext onDragEnd={handlerDragEnd}>
        <div className={classes.view}>
          {Object.entries(pipelines).map(([key, value], index) =>
            shouldShowColumn(key) && columnsState[key] ? (
              <PipelineColumn
                key={key + index}
                items={value}
                progressOfGetBlockedAmbassadors={
                  progressOfGetAllAmbassadors.blocked
                }
                isLoading={
                  key === 'refused'
                    ? progressOfGetAllAmbassadors['blocked']
                      ? progressOfGetAllAmbassadors['blocked']['isLoading']
                        ? true
                        : false
                      : false
                    : key === 'interested'
                    ? interestedBAStatus === FETCH_BA_STATUS.LOADING
                      ? true
                      : false
                    : key === 'uninterested'
                    ? uninterestedBAStatus === FETCH_BA_STATUS.LOADING
                      ? true
                      : false
                    : false
                }
                onExportData={onExportData}
                onMailClick={onMailClick}
                onDelete={handleDelete}
                onApprovedModalOpen={handleApprovedModalOpen}
                onDocumentsModalOpen={handleApprovedModalOpen}
                onAttendantUpdate={handleAttendantUpdate}
                name={key}
                url={url}
                isESCountry={isESCountry}
              />
            ) : null,
          )}
        </div>
      </DragDropContext>
      {(!ambassadorsTransformed || progressOfGetAllAmbassadors.isLoading) && (
        <Progress label={progressOfGetAllAmbassadors.label} />
      )}
    </div>
  )
}

const validateStatusMovement = (from, to, userId, pipelines) => {
  if (!to) {
    return false
  }

  if (from.droppableId === to.droppableId) {
    return false
  }

  if (
    to.droppableId === 'videoOnboarding' ||
    from.droppableId === 'videoOnboarding'
  ) {
    return false
  }
  if (to.droppableId === 'booked' && from.droppableId === 'onSiteTraining') {
    return false
  }

  if (
    to.droppableId === 'interested' &&
    from.droppableId === 'onSiteTraining'
  ) {
    return true
  }

  if (
    to.droppableId === 'uninterested' &&
    from.droppableId === 'onSiteTraining'
  ) {
    return true
  }

  if (to.droppableId === 'refused') {
    return true
  }

  if (from.droppableId === 'booked' && to.droppableId === 'preAccess') {
    return true
  }

  if (from.droppableId === 'booked' && to.droppableId === 'test') {
    return true
  }

  if (from.droppableId === 'booked' && to.droppableId === 'uninterested') {
    return true
  }
  if (from.droppableId === 'booked' && to.droppableId === 'onSiteTraining') {
    return true
  }

  if (from.droppableId === 'interested' && to.droppableId === 'uninterested') {
    return true
  }

  if (
    from.droppableId === 'onSiteTraining' &&
    ['interested', 'booked', 'refused', 'blocked', 'preAccess'].includes(
      to.droppableId,
    )
  ) {
    return true
  }

  if (
    to.droppableId === 'onSiteTraining' &&
    ['interested', 'booked', 'refused', 'blocked', 'preAccess'].includes(
      from.droppableId,
    )
  ) {
    return true
  }

  if (from.droppableId === 'test' && to.droppableId === 'preAccess') {
    return true
  }

  if (from.droppableId === 'test' && to.droppableId === 'uninterested') {
    return true
  }

  if (from.droppableId === 'interested' && to.droppableId === 'preAccess') {
    return true
  }

  if (from.droppableId === 'interested' && to.droppableId === 'test') {
    return false
  }

  if (
    from.droppableId === 'finalReview' &&
    to.droppableId === 'onsiteMeeting'
  ) {
    return true
  }

  if (from.droppableId === 'onsiteMeeting' && to.droppableId === 'approved') {
    return true
  }

  if (from.droppableId === 'finalReview' && to.droppableId === 'approved') {
    return false
  }

  if (from.droppableId === 'preAccess' && to.droppableId === 'approved') {
    return true
  }

  if (from.droppableId === 'uninterested' && to.droppableId === 'interested') {
    const { itemData } = pipelines[from.droppableId].reduce(
      (acc, item, index) => {
        if (item.id == userId) return { itemData: item, index }
        return acc
      },
      {
        itemData: {},
        index: 0,
      },
    )

    const uninterestedDate = moment(Number(itemData.uninterestedDate))
    const lastSunday = moment.utc().startOf('week')
    const isUserResetted = uninterestedDate.isBefore(lastSunday)
    if (
      (itemData.previousStage &&
        itemData.previousStage.toLowerCase() === 'interested') ||
      isUserResetted
    ) {
      return true
    }

    return false
  }

  if (from.droppableId === 'uninterested' && to.droppableId === 'booked') {
    const { itemData } = pipelines[from.droppableId].reduce(
      (acc, item, index) => {
        if (item.id == userId) return { itemData: item, index }
        return acc
      },
      {
        itemData: {},
        index: 0,
      },
    )

    const uninterestedDate = moment(Number(itemData.uninterestedDate))
    const lastSunday = moment.utc().startOf('week')
    const isUserResetted = uninterestedDate.isBefore(lastSunday)
    if (
      itemData.previousStage &&
      itemData.previousStage.toLowerCase() === 'booked' &&
      !isUserResetted
    ) {
      return true
    }

    return false
  }

  if (from.droppableId === 'uninterested' && to.droppableId === 'test') {
    const { itemData } = pipelines[from.droppableId].reduce(
      (acc, item, index) => {
        if (item.id == userId) return { itemData: item, index }
        return acc
      },
      {
        itemData: {},
        index: 0,
      },
    )

    const uninterestedDate = moment(Number(itemData.uninterestedDate))
    const lastSunday = moment.utc().startOf('week')
    const isUserResetted = uninterestedDate.isBefore(lastSunday)
    if (
      itemData.previousStage &&
      itemData.previousStage.toLowerCase() === 'test' &&
      !isUserResetted
    ) {
      return true
    }

    return false
  }

  if (
    from.droppableId === 'uninterested' &&
    to.droppableId === 'onSiteTraining'
  ) {
    const { itemData } = pipelines[from.droppableId].reduce(
      (acc, item, index) => {
        if (item.id == userId) return { itemData: item, index }
        return acc
      },
      {
        itemData: {},
        index: 0,
      },
    )

    const uninterestedDate = moment(Number(itemData.uninterestedDate))
    const lastSunday = moment.utc().startOf('week')
    const isUserResetted = uninterestedDate.isBefore(lastSunday)
    if (
      itemData.previousStage &&
      itemData.previousStage.toLowerCase() === 'onSiteTraining' &&
      !isUserResetted
    ) {
      return true
    }

    return false
  }
}

export const dateFormatter = memberSince => {
  return moment(memberSince).format('D-MMM-YYYY')
}

export default memo(withStyles(style)(withRouter(AmbassadorsPipelineView)))
